import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MainHeader } from "@/components/layout/MainHeader";
import { Helmet } from "react-helmet";

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Terms of Service - Mai@ Email Assistant</title>
        <meta name="description" content="Terms of Service for Mai@ Email Assistant. Read about our terms of use, service conditions, and user agreements." />
        <meta property="og:title" content="Terms of Service - Mai@ Email Assistant" />
        <meta property="og:description" content="Terms of Service for Mai@ Email Assistant. Read about our terms of use, service conditions, and user agreements." />
        <meta property="og:url" content="https://maia-email.com/terms-of-service" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/maia-og.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms of Service - Mai@ Email Assistant" />
        <meta name="twitter:description" content="Terms of Service for Mai@ Email Assistant. Read about our terms of use, service conditions, and user agreements." />
        <meta name="twitter:image" content="/maia-og.jpg" />
        <link rel="canonical" href="https://maia-email.com/terms-of-service" />
      </Helmet>

      <MainHeader />
      <div className="container mx-auto py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="prose prose-sm md:prose-base lg:prose-lg dark:prose-invert max-w-none">
          <h1>Terms of Service</h1>
          <p>Last updated: {new Date().toLocaleDateString()}</p>

          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and using Maia Email Assistant Web Service, you agree to be bound by these Terms of Service. 
            If you do not agree to these terms, please do not use our service.
          </p>

          <h2>2. Description of Service</h2>
          <p>
            Maia is an AI-powered email assistance service that helps users manage and respond to their emails more efficiently. 
            The service includes:
          </p>
          <ul>
            <li>Email processing and analysis</li>
            <li>AI-generated response suggestions</li>
            <li>Integration with email providers</li>
            <li>Document processing capabilities</li>
          </ul>

          <h2>3. User Accounts</h2>
          <p>
            To use Maia, you must:
          </p>
          <ul>
            <li>Create an account with valid credentials</li>
            <li>Maintain the security of your account</li>
            <li>Notify us immediately of any unauthorized access</li>
            <li>Be at least 13 years old to use the service</li>
          </ul>

          <h2>4. Token System</h2>
          <p>
            Our service operates on a token-based system:
          </p>
          <ul>
            <li>Users receive an initial allocation of tokens upon registration</li>
            <li>Tokens are consumed when using AI features</li>
            <li>Additional tokens can be purchased</li>
            <li>Token balances and transactions are tracked in your account</li>
          </ul>

          <h2>5. Data Usage and Privacy</h2>
          <p>
            We process your data in accordance with our Privacy Policy:
          </p>
          <ul>
            <li>Email content is processed through secure channels</li>
            <li>Documents are handled with strict confidentiality</li>
            <li>Data is processed using OpenAI's services</li>
            <li>User data is stored securely in Supabase</li>
          </ul>

          <h2>6. User Responsibilities</h2>
          <p>
            When using Maia, you agree to:
          </p>
          <ul>
            <li>Provide accurate information</li>
            <li>Use the service in compliance with applicable laws</li>
            <li>Not misuse or attempt to exploit the service</li>
            <li>Not share your account credentials</li>
          </ul>

          <h2>7. Service Limitations</h2>
          <p>
            Please be aware of the following limitations:
          </p>
          <ul>
            <li>AI-generated responses are suggestions only</li>
            <li>Service availability may vary</li>
            <li>Token consumption rates may change</li>
            <li>Feature availability may depend on your subscription level</li>
          </ul>

          <h2>8. Intellectual Property</h2>
          <p>
            All content and features of Maia, including but not limited to:
          </p>
          <ul>
            <li>Software code and algorithms</li>
            <li>User interface design</li>
            <li>Branding and logos</li>
            <li>Documentation and content</li>
          </ul>
          <p>
            are the exclusive property of MLC CONSULTING - UNMANNED EXPERT S.L. and are protected by intellectual property laws.
          </p>

          <h2>9. Modifications to Service</h2>
          <p>
            We reserve the right to:
          </p>
          <ul>
            <li>Modify or discontinue features</li>
            <li>Update pricing and token systems</li>
            <li>Change service terms with notice</li>
            <li>Improve or modify the AI systems</li>
          </ul>

          <h2>10. Termination</h2>
          <p>
            We may terminate or suspend access to our service immediately, without prior notice or liability, for any reason, including:
          </p>
          <ul>
            <li>Violation of Terms</li>
            <li>Fraudulent activity</li>
            <li>Misuse of the service</li>
            <li>Non-payment of fees</li>
          </ul>

          <h2>11. Contact Information</h2>
          <div className="bg-secondary/20 p-6 rounded-lg my-4">
            <h3 className="text-lg font-semibold mb-2">Company Information</h3>
            <div className="space-y-2">
              <p className="font-medium">MLC CONSULTING - UNMANNED EXPERT S.L.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-muted-foreground">Tax ID (CIF)</p>
                  <p className="font-medium">B40577181</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <a href="mailto:info@mlcconsulting.es" className="text-primary hover:underline">
                    info@mlcconsulting.es
                  </a>
                </div>
              </div>
              <div>
                <p className="text-muted-foreground">Registered Address</p>
                <p className="font-medium">
                  Carrer Les Masses 13<br />
                  46117 Betera (Valencia)<br />
                  SPAIN
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;